import { API_BASE_URL, getAuthToken } from '../config/constants';
// Only log in development to avoid console spam in production
const DEBUG = process.env.NODE_ENV !== 'production';

// URL'den gelen QR token'ını al
const getUrlToken = (): string => {
  try {
    if (typeof window === 'undefined') return '';
    const sp = new URLSearchParams(window.location.search);
    return sp.get('token') || '';
  } catch {
    return '';
  }
};

// İstek header'larını standartlaştır
const buildHeaders = (extra: Record<string, string> = {}) => {
  const selectedDatabaseId = localStorage.getItem('selectedDatabaseId') || '';
  const urlToken = getUrlToken();
  const jwtToken = getAuthToken() || '';

  const base: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(selectedDatabaseId ? { 'X-Database-Id': selectedDatabaseId } : {})
  };

  if (urlToken) {
    // QR modunda Temp token gönder, Authorization gönderme
    base['X-Temp-Token'] = urlToken;
  } else if (jwtToken) {
    base['Authorization'] = `Bearer ${jwtToken}`;
  }

  return { ...base, ...extra };
};

// Müşteri özellik tiplerini getiren fonksiyon
export const getCustomerPropertyTypes = async () => {
  try {
    if (DEBUG) console.log('Müşteri özellik tipleri için API isteği yapılıyor...');
    // Backend'den aktif özellikleri al
    // Header'lar: QR token varsa X-Temp-Token, yoksa Bearer JWT
    const response = await fetch(`${API_BASE_URL}/api/CustomerProperty/types`, {
      method: 'GET',
      headers: buildHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Müşteri özellik tipleri alınırken bir hata oluştu');
    }
    
    const data = await response.json();
    if (DEBUG) console.log('API yanıtı - Müşteri özellik tipleri:', data);
    
    // Normalize: UI expects typeName and typeCode
    return data.map((type: any) => ({
      typeCode: type.typeCode ?? type.attributeTypeCode ?? type.currAccTypeCode,
      typeName: type.typeDescription ?? type.description ?? type.typeName,
      isRequired: type.isRequired ?? false,
      isBlocked: type.isBlocked ?? false,
      valueType: 'select' // Varsayılan
    }));
  } catch (error) {
    if (DEBUG) console.error('Müşteri özellik tipleri API hatası:', error);
    throw error;
  }
};

// Belirli bir özellik tipi için değerleri getiren fonksiyon
export const getPropertyValuesByType = async (attributeTypeCode: number) => {
  try {
    if (DEBUG) console.log(`${attributeTypeCode} tipindeki özellik değerleri için API isteği yapılıyor...`);
    // Backend'den özellik değerlerini al
    // Header'lar: QR token varsa X-Temp-Token, yoksa Bearer JWT
    const response = await fetch(`${API_BASE_URL}/api/CustomerProperty/values/${attributeTypeCode}`, {
      method: 'GET',
      headers: buildHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`${attributeTypeCode} tipindeki özellik değerleri alınırken bir hata oluştu`);
    }
    
    const data = await response.json();
    if (DEBUG) console.log(`API yanıtı - ${attributeTypeCode} tipindeki özellik değerleri:`, data);
    
    // API'den gelen verileri dönüştür
    return data.map((item: any) => ({
      valueCode: item.attributeCode ?? item.valueCode ?? item.code,
      valueName: item.attributeDescription ?? item.description ?? item.valueName ?? item.name
    }));
  } catch (error) {
    if (DEBUG) console.error(`${attributeTypeCode} tipindeki özellik değerleri API hatası:`, error);
    throw error;
  }
};

// Müşteri özelliklerini getiren fonksiyon (belirli bir müşteri için)
export const getCustomerPropertiesByCustomerCode = async (customerCode: string) => {
  try {
    if (DEBUG) console.log(`${customerCode} kodlu müşteri özellikleri için API isteği yapılıyor...`);
    // Backend'den müşteri özelliklerini al
    // Header'lar: QR token varsa X-Temp-Token, yoksa Bearer JWT
    const response = await fetch(`${API_BASE_URL}/api/CustomerProperty/${customerCode}`, {
      method: 'GET',
      headers: buildHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`${customerCode} kodlu müşterinin özellikleri alınırken bir hata oluştu`);
    }
    
    const data = await response.json();
    if (DEBUG) console.log(`API yanıtı - ${customerCode} kodlu müşteri özellikleri:`, data);
    return data;
  } catch (error) {
    if (DEBUG) console.error(`${customerCode} kodlu müşteri özellikleri API hatası:`, error);
    throw error;
  }
};

// Müşteri özelliklerini kaydetmek için fonksiyon
export const saveCustomerProperties = async (customerCode: string, properties: Record<number, string>, options?: { requestId?: string }) => {
  try {
    if (DEBUG) console.log(`${customerCode} kodlu müşteri özellikleri kaydediliyor...`, properties);
    // Backend'e müşteri özelliklerini kaydet
    // Header'lar: QR token varsa X-Temp-Token, yoksa Bearer JWT
    const response = await fetch(`${API_BASE_URL}/api/CustomerProperty/${customerCode}`, {
      method: 'POST',
      headers: buildHeaders({ ...(options?.requestId ? { 'X-Request-Id': options.requestId } : {}) }),
      body: JSON.stringify(properties)
    });
    
    if (!response.ok) {
      throw new Error('Müşteri özellikleri kaydedilirken bir hata oluştu');
    }
    
    const data = await response.json();
    if (DEBUG) console.log(`API yanıtı - ${customerCode} kodlu müşteri özellikleri kaydedildi:`, data);
    return data;
  } catch (error) {
    if (DEBUG) console.error(`${customerCode} kodlu müşteri özellikleri kaydetme API hatası:`, error);
    throw error;
  }
};

// Müşteri özelliğini silmek için fonksiyon
export const deleteCustomerProperty = async (customerCode: string, attributeTypeCode: number) => {
  try {
    if (DEBUG) console.log(`${customerCode} kodlu müşterinin ${attributeTypeCode} tipindeki özelliği siliniyor...`);
    // Backend'den müşteri özelliğini sil
    // Header'lar: QR token varsa X-Temp-Token, yoksa Bearer JWT
    const response = await fetch(`${API_BASE_URL}/api/CustomerProperty/${customerCode}/${attributeTypeCode}`, {
      method: 'DELETE',
      headers: buildHeaders()
    });
    
    if (!response.ok) {
      throw new Error('Müşteri özelliği silinirken bir hata oluştu');
    }
    
    const data = await response.json();
    if (DEBUG) console.log(`API yanıtı - ${customerCode} kodlu müşterinin ${attributeTypeCode} tipindeki özelliği silindi:`, data);
    return data;
  } catch (error) {
    if (DEBUG) console.error(`${customerCode} kodlu müşterinin ${attributeTypeCode} tipindeki özelliği silme API hatası:`, error);
    throw error;
  }
};
